import { useState } from 'react'
import { reviewApi, type ReviewSource } from '../api/review'
import { createAuthor } from '../lib/api'
import { readApiError } from '../lib/apiError'
import type { Author } from '../types'

const MISSING_LABELS: Record<string, string> = {
  isbn: 'ISBN fehlt',
  doi: 'DOI fehlt',
  issn: 'ISSN fehlt',
  publisher: 'Verlag fehlt',
  year: 'Jahr fehlt',
  authors: 'kein Autor',
}

interface ReviewSourceItemProps {
  source: ReviewSource
  authors: Author[]
  onSaved: () => void
  onAuthorCreated: (author: Author) => void
}

export const ReviewSourceItem = ({
  source,
  authors,
  onSaved,
  onAuthorCreated,
}: ReviewSourceItemProps) => {
  const [isbn, setIsbn] = useState(source.isbn ?? '')
  const [doi, setDoi] = useState(source.doi ?? '')
  const [issn, setIssn] = useState(source.issn ?? '')
  const [publisher, setPublisher] = useState(source.publisher ?? '')
  const [year, setYear] = useState(source.year != null ? String(source.year) : '')
  const [selectedAuthors, setSelectedAuthors] = useState(source.authors)
  const [authorInput, setAuthorInput] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isBook = source.type === 'book'
  const isMissing = (field: string) => source.missing.includes(field)

  const addAuthor = async () => {
    const name = authorInput.trim()
    if (!name) return
    if (selectedAuthors.some((a) => a.name.toLowerCase() === name.toLowerCase())) {
      setAuthorInput('')
      return
    }

    const existing = authors.find((a) => a.name.toLowerCase() === name.toLowerCase())
    if (existing) {
      setSelectedAuthors([...selectedAuthors, { id: existing.id, name: existing.name }])
      setAuthorInput('')
      return
    }

    try {
      const created = await createAuthor({ name })
      onAuthorCreated(created.data)
      setSelectedAuthors([...selectedAuthors, { id: created.data.id, name: created.data.name }])
      setAuthorInput('')
    } catch (err) {
      setError(readApiError(err, 'Autor konnte nicht angelegt werden.'))
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    setError(null)
    try {
      await reviewApi.updateSource(source.id, {
        type: source.type,
        title: source.title,
        isbn: isbn.trim() || null,
        doi: doi.trim() || null,
        issn: issn.trim() || null,
        publisher: publisher.trim() || null,
        year: year.trim() ? Number(year) : null,
        author_ids: selectedAuthors.map((a) => a.id),
      })
      onSaved()
    } catch (err) {
      setError(readApiError(err))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <article className="review-item">
      <header className="review-item-header">
        <div>
          <span className="review-item-id">{isBook ? 'Buch' : 'Artikel'} #{source.id}</span>
          <span className="review-item-source">{source.title}</span>
        </div>
        <div className="review-badges">
          {source.missing.map((key) => (
            <span key={key} className="review-badge">
              {MISSING_LABELS[key] ?? key}
            </span>
          ))}
          <a
            href={`/sources/${source.id}`}
            target="_blank"
            rel="noreferrer"
            className="review-item-link"
          >
            Öffnen ↗
          </a>
        </div>
      </header>

      <div className="review-item-body">
        <div className="review-fields">
          <div className="review-field-grid">
            {isBook ? (
              <>
                <div className={isMissing('isbn') ? 'review-field is-missing' : 'review-field'}>
                  <label className="review-label" htmlFor={`isbn-${source.id}`}>ISBN</label>
                  <input
                    id={`isbn-${source.id}`}
                    value={isbn}
                    onChange={(e) => setIsbn(e.target.value)}
                    disabled={isSaving}
                  />
                </div>
                <div className={isMissing('publisher') ? 'review-field is-missing' : 'review-field'}>
                  <label className="review-label" htmlFor={`publisher-${source.id}`}>Verlag</label>
                  <input
                    id={`publisher-${source.id}`}
                    value={publisher}
                    onChange={(e) => setPublisher(e.target.value)}
                    disabled={isSaving}
                  />
                </div>
              </>
            ) : (
              <>
                <div className={isMissing('doi') ? 'review-field is-missing' : 'review-field'}>
                  <label className="review-label" htmlFor={`doi-${source.id}`}>DOI</label>
                  <input
                    id={`doi-${source.id}`}
                    value={doi}
                    onChange={(e) => setDoi(e.target.value)}
                    disabled={isSaving}
                  />
                </div>
                <div className={isMissing('issn') ? 'review-field is-missing' : 'review-field'}>
                  <label className="review-label" htmlFor={`issn-${source.id}`}>ISSN</label>
                  <input
                    id={`issn-${source.id}`}
                    value={issn}
                    onChange={(e) => setIssn(e.target.value)}
                    disabled={isSaving}
                  />
                </div>
              </>
            )}

            <div className={isMissing('year') ? 'review-field is-missing' : 'review-field'}>
              <label className="review-label" htmlFor={`year-${source.id}`}>Jahr</label>
              <input
                id={`year-${source.id}`}
                type="number"
                min={1}
                max={2999}
                value={year}
                onChange={(e) => setYear(e.target.value)}
                disabled={isSaving}
              />
            </div>
          </div>

          <div className={isMissing('authors') ? 'review-field is-missing' : 'review-field'}>
            <label className="review-label" htmlFor={`author-${source.id}`}>Autoren</label>
            <div className="tag-input-container">
              {selectedAuthors.map((author) => (
                <span key={author.id} className="tag-pill">
                  {author.name}
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedAuthors(selectedAuthors.filter((a) => a.id !== author.id))
                    }
                    disabled={isSaving}
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                id={`author-${source.id}`}
                list={`author-options-${source.id}`}
                value={authorInput}
                onChange={(e) => setAuthorInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addAuthor()
                  }
                }}
                placeholder={selectedAuthors.length === 0 ? 'Name + Enter' : ''}
                disabled={isSaving}
              />
              <datalist id={`author-options-${source.id}`}>
                {authors.map((author) => (
                  <option key={author.id} value={author.name} />
                ))}
              </datalist>
            </div>
          </div>

          {error && <p className="review-error">{error}</p>}

          <div className="review-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Wird gespeichert…' : 'Speichern'}
            </button>
          </div>
        </div>
      </div>
    </article>
  )
}
